import { useCallback, useEffect, useState, type ChangeEvent, type ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button } from '../../../../shared/components/index.js';

/**
 * M08-009 §16 — Vendor document register for a job.
 *
 * Routed by the vendor assignment id; resolves jobRequestId from the assignment
 * detail. Lists documents (GET /v1/vendor/documents), registers a new document
 * (POST /v1/vendor/documents) and archives one (DELETE /v1/vendor/documents/:id).
 * A selected file auto-fills filename / mimeType / sizeBytes for the frozen
 * UploadDocInput contract; storageUri references the stored object.
 */
interface DocumentRow {
  readonly documentId: string;
  readonly documentType: string;
  readonly filename: string;
  readonly mimeType: string;
  readonly sizeBytes: string;
  readonly version: number;
  readonly uploadedAt: string;
}

const DOC_TYPES = ['WORK_ORDER', 'SITE_NOTES', 'MATERIAL_NOTES', 'APPROVAL_DOC', 'SUPPORTING', 'BEFORE_PHOTO', 'AFTER_PHOTO', 'SITE_PHOTO'] as const;

export function VendorDocumentsPage(): ReactNode {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const { token } = useAuth();
  const { t } = useTranslation();

  const [jobRequestId, setJobRequestId] = useState<string | null>(null);
  const [docs, setDocs] = useState<readonly DocumentRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const [documentType, setDocumentType] = useState<(typeof DOC_TYPES)[number]>('WORK_ORDER');
  const [filename, setFilename] = useState('');
  const [mimeType, setMimeType] = useState('');
  const [sizeBytes, setSizeBytes] = useState(0);
  const [storageUri, setStorageUri] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadDocs = useCallback(async (job: string) => {
    const r = await apiRequest<{ items: DocumentRow[] }>(`/v1/vendor/documents?jobRequestId=${encodeURIComponent(job)}`, { token });
    setDocs(r.items);
  }, [token]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const asg = await apiRequest<{ jobRequestId: string }>(`/v1/vendor-assignments/${assignmentId}`, { token });
        if (!alive) return;
        setJobRequestId(asg.jobRequestId);
        await loadDocs(asg.jobRequestId);
        if (alive) setLoaded(true);
      } catch (e) {
        if (alive) { setError((e as Error).message); setLoaded(true); }
      }
    })();
    return () => { alive = false; };
  }, [assignmentId, token, loadDocs]);

  function onFile(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setFilename(f.name);
    setMimeType(f.type || 'application/octet-stream');
    setSizeBytes(f.size);
  }

  async function upload() {
    setFormError(null);
    if (!filename) { setFormError(t('vops.documents.fileRequired')); return; }
    if (!storageUri.trim()) { setFormError(t('vops.documents.uriRequired')); return; }
    setBusy(true);
    try {
      await apiRequest('/v1/vendor/documents', {
        method: 'POST',
        token,
        body: {
          jobRequestId,
          documentType,
          filename,
          mimeType,
          sizeBytes,
          storageUri: storageUri.trim(),
        },
      });
      setOk(true);
      setFilename('');
      setMimeType('');
      setSizeBytes(0);
      setStorageUri('');
      if (jobRequestId) await loadDocs(jobRequestId);
    } catch (e) {
      setFormError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function archive(documentId: string) {
    setError(null);
    setBusy(true);
    try {
      await apiRequest(`/v1/vendor/documents/${documentId}`, { method: 'DELETE', token });
      if (jobRequestId) await loadDocs(jobRequestId);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!loaded) return <main aria-label="vendor-documents"><p>{t('common.loading')}</p></main>;

  return (
    <main aria-label="vendor-documents">
      <h1>{t('vops.documents.title')}</h1>
      <p><Link to={`/vendor-ops/jobs/${assignmentId}`} aria-label="back-to-job">{t('vops.common.backToJob')}</Link></p>

      {error ? <p role="alert">{error}</p> : null}

      <section aria-label="documents-list-section">
        <h2>{t('vops.documents.uploaded')}</h2>
        {docs.length === 0 ? (
          <p aria-label="documents-empty">{t('vops.documents.empty')}</p>
        ) : (
          <ul aria-label="documents-list">
            {docs.map((d) => (
              <li key={d.documentId} aria-label={`document-${d.documentId}`}>
                <span aria-label={`document-type-${d.documentId}`}>{d.documentType}</span>{' · '}
                {d.filename} (v{d.version})
                <Button
                  type="button"
                  aria-label={`document-delete-${d.documentId}`}
                  onClick={() => archive(d.documentId)}
                  disabled={busy}
                  variant="danger"
                >
                  {t('vops.documents.archive')}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-label="documents-upload">
        <h2>{t('vops.documents.register')}</h2>
        {ok ? <p role="status" aria-label="upload-result">{t('vops.documents.registered')}</p> : null}
        {formError ? <p role="alert">{formError}</p> : null}

        <label htmlFor="doc-type">{t('vops.documents.docType')}</label>
        <select id="doc-type" aria-label="doc-type" value={documentType}
                onChange={(e) => setDocumentType(e.target.value as (typeof DOC_TYPES)[number])} disabled={busy}>
          {DOC_TYPES.map((dt) => <option key={dt} value={dt}>{dt}</option>)}
        </select>

        <label htmlFor="doc-file">{t('vops.documents.file')}</label>
        <input id="doc-file" type="file" aria-label="doc-file" onChange={onFile} disabled={busy} />
        {filename ? <p aria-label="doc-selected">{t('vops.documents.selected', { filename, mimeType, sizeBytes })}</p> : null}

        <label htmlFor="doc-uri">{t('vops.documents.storageUri')}</label>
        <input id="doc-uri" type="text" aria-label="doc-uri" value={storageUri}
               onChange={(e) => setStorageUri(e.target.value)} disabled={busy} />

        <Button type="button" aria-label="upload-btn" onClick={upload} disabled={busy} variant="primary">{t('vops.documents.registerBtn')}</Button>
      </section>
    </main>
  );
}
